import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import ImageUploader from "@/components/ImageUploader";
import { Modal } from "./Projects";
import { Plus, Trash2, Edit3 } from "lucide-react";

const blank = {
  name: "", description: "", sqft: 2000, bedrooms: 3, bathrooms: 2.0,
  garage: 2, stories: 1, price_from: 350000, image_url: "", plan_image_url: "",
  features: [], featured: false,
};

export default function AdminFloorPlans() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/floor-plans").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...editing,
      sqft: Number(editing.sqft),
      bedrooms: Number(editing.bedrooms),
      bathrooms: Number(editing.bathrooms),
      garage: Number(editing.garage),
      stories: Number(editing.stories),
      price_from: Number(editing.price_from),
    };
    if (editing.id) {
      await api.put(`/floor-plans/${editing.id}`, payload);
    } else {
      await api.post("/floor-plans", payload);
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this floor plan?")) return;
    await api.delete(`/floor-plans/${id}`);
    load();
  };

  return (
    <DashboardLayout
      role="admin" title="Floor Plans"
      subtitle="Stock plans new-construction buyers can choose from on your site."
      actions={<button className="btn-primary" onClick={() => setEditing({ ...blank })}><Plus size={16}/> Add Plan</button>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <div key={p.id} className="card overflow-hidden">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover"/>
            </div>
            <div className="p-5">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-display text-lg font-bold">{p.name}</h3>
                <div className="text-sm font-semibold" style={{color:'var(--accent)'}}>{money(p.price_from)}</div>
              </div>
              <div className="text-xs mb-4" style={{color:'var(--text-muted)'}}>
                {p.sqft.toLocaleString()} sqft · {p.bedrooms}br · {p.bathrooms}ba · {p.garage}-car
              </div>
              <div className="flex gap-2">
                <button className="btn-ghost !py-1.5 !px-3 !text-xs flex-1 justify-center" onClick={() => setEditing(p)}><Edit3 size={12}/> Edit</button>
                <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => remove(p.id)} style={{color:'#fca5a5'}}><Trash2 size={12}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Floor Plan" : "New Floor Plan"}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Plan name" full><input className="input" value={editing.name} onChange={(e)=>setEditing({...editing, name: e.target.value})}/></Field>
            <Field label="Square feet"><input type="number" className="input" value={editing.sqft} onChange={(e)=>setEditing({...editing, sqft: e.target.value})}/></Field>
            <Field label="Stories"><input type="number" className="input" value={editing.stories} onChange={(e)=>setEditing({...editing, stories: e.target.value})}/></Field>
            <Field label="Bedrooms"><input type="number" className="input" value={editing.bedrooms} onChange={(e)=>setEditing({...editing, bedrooms: e.target.value})}/></Field>
            <Field label="Bathrooms"><input type="number" step="0.5" className="input" value={editing.bathrooms} onChange={(e)=>setEditing({...editing, bathrooms: e.target.value})}/></Field>
            <Field label="Garage (cars)"><input type="number" className="input" value={editing.garage} onChange={(e)=>setEditing({...editing, garage: e.target.value})}/></Field>
            <Field label="Price from (USD)"><input type="number" className="input" value={editing.price_from} onChange={(e)=>setEditing({...editing, price_from: e.target.value})}/></Field>
            <Field label="Hero image (exterior/rendering)" full>
              <ImageUploader
                value={editing.image_url}
                onChange={(url) => setEditing({...editing, image_url: url})}
                folder="coastal-bend/floor-plans"
                aspect="aspect-[16/10]"
                testid="plan-hero"
              />
            </Field>
            <Field label="Floor plan blueprint image" full>
              <ImageUploader
                value={editing.plan_image_url}
                onChange={(url) => setEditing({...editing, plan_image_url: url})}
                folder="coastal-bend/floor-plans"
                aspect="aspect-[16/9]"
                testid="plan-blueprint"
              />
            </Field>
            <Field label="Description" full><textarea className="input" rows={3} value={editing.description} onChange={(e)=>setEditing({...editing, description: e.target.value})}/></Field>
            <Field label="Features (one per line)" full>
              <textarea className="input" rows={4} value={editing.features?.join('\n') || ''} onChange={(e)=>setEditing({...editing, features: e.target.value.split('\n').map(s=>s.trim()).filter(Boolean)})}/>
            </Field>
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <input type="checkbox" checked={!!editing.featured} onChange={(e)=>setEditing({...editing, featured: e.target.checked})}/>
              Feature on homepage
            </label>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn-primary" onClick={save}>Save Plan</button>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`block ${full ? 'md:col-span-2' : ''}`}>
      <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>{label}</div>
      {children}
    </label>
  );
}
